import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import type { Tone } from '../types';
import styles from './record.module.css';

/** The prototype's `Badge` (4/10, 12 px, tracked; accent carries a border) used in the record head and rows. */
export function Badge({
  tone,
  children,
  className,
}: {
  tone: Tone;
  children: ReactNode;
  className?: string;
}) {
  const toneClass = {
    neutral: styles.badgeNeutral,
    accent: styles.badgeAccent,
    success: styles.badgeSuccess,
    warning: styles.badgeWarning,
    danger: styles.badgeDanger,
    info: styles.badgeInfo,
  }[tone];
  return <span className={clsx(styles.badge, toneClass, className)}>{children}</span>;
}

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

/** The record banner of Kontaktakte I.08–I.11 (the prototype's `Alert` with an action row). */
export function RecordAlert({
  tone,
  title,
  text,
  actions,
}: {
  tone: AlertTone;
  title: string;
  text: ReactNode;
  actions?: ReactNode | undefined;
}) {
  const toneClass = {
    info: styles.alertInfo,
    warning: styles.alertWarning,
    danger: styles.alertDanger,
    success: styles.alertSuccess,
  }[tone];
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={clsx(styles.alert, toneClass)}
    >
      <div className={styles.alertBody}>
        <strong className={styles.alertTitle}>{title}</strong>
        <span className={styles.alertText}>
          {text}
          {actions ? <span className={styles.alertActions}>{actions}</span> : null}
        </span>
      </div>
    </div>
  );
}

export function PaperButton({
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type={type} className={clsx(styles.paperButton, className)} {...rest} />;
}

export function GhostButton({
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type={type} className={clsx(styles.ghostButton, className)} {...rest} />;
}

/** The prototype's `EmptyState` in its three presets (side card, tight group, big timeline). */
export function EmptyBox({
  title,
  text,
  variant = 'side',
}: {
  title: string;
  text?: string | null | undefined;
  variant?: 'side' | 'tight' | 'big' | undefined;
}) {
  return (
    <div
      className={clsx(
        styles.empty,
        variant === 'tight' && styles.emptyTight,
        variant === 'big' && styles.emptyBig,
      )}
    >
      <strong className={styles.emptyTitle}>{title}</strong>
      {text ? <p className={styles.emptyText}>{text}</p> : null}
    </div>
  );
}

/** The origin chip ("Kontakt", "Deal #4192"); everything except "Kontakt" is accent. */
export function OriginChip({ label, sunken = false }: { label: string; sunken?: boolean | undefined }) {
  return (
    <span
      className={clsx(
        styles.originChip,
        label !== 'Kontakt' && styles.originChipAccent,
        sunken && styles.originChipSunken,
      )}
    >
      {label}
    </span>
  );
}

const KIND_CLASS: Record<Tone, string | undefined> = {
  accent: styles.kindAccent,
  info: styles.kindInfo,
  warning: styles.kindWarning,
  danger: styles.kindDanger,
  success: styles.kindSuccess,
  neutral: styles.kindNeutral,
};

/** Uppercase kind chip of the "Steht an" items and the record rows ("AUFGABE", "TERMIN", "ANRUF"). */
export function KindChip({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={clsx(styles.kindChip, KIND_CLASS[tone])}>{children}</span>;
}
